import { useSyncExternalStore } from 'react';
import { getUiScale, subscribeToUiScale } from '@/lib/uiScale';
import type { UiScale } from '@shared/enums.js';

/**
 * The one production mechanism for scale-aware layout decisions.
 *
 * Phase 6 made the whole interface grow by changing the root font size, so most
 * layouts need nothing here — they scale in `rem` automatically. This hook is
 * for the exceptions the mockups call out: layouts that must change *shape*, not
 * merely size, at an enlarged scale. Five KPI cards fit across a 1440px viewport
 * at 100% and cannot at 140%; the Leads filter rail is worth showing by default
 * at 100% and worth collapsing at 125%+.
 *
 * It reads the same source of truth the selector and the root element use
 * (`getUiScale` / `subscribeToUiScale`), so a component re-renders the instant
 * the user changes scale — no second preference store, no `data-ui-scale`
 * polling, no viewport media query (which cannot even see `html { font-size }`
 * and so would never fire for this).
 */
export function useUiScale(): UiScale {
  return useSyncExternalStore(subscribeToUiScale, getUiScale, getUiScale);
}

/**
 * True at 125% and above — the point where a 1440px viewport stops having room
 * for the widest layouts and they need to change shape rather than just grow.
 * The single definition of "enlarged" so screens agree on the threshold.
 */
export function useIsEnlarged(): boolean {
  return useUiScale() >= 125;
}
